import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { Store } from '@ngxs/store';
import { parseRfc3339 } from '@libs/rfc3339';
import { AuthState } from '@apps/clos-sessions/state/auth';
import { createSessionOperations } from './create-session-operation';
import { environment } from '@apps/clos-sessions/environments';
import { ClosSession, ClosSessionMedia, ClosStorage } from './types';
import {
  ClosMediaRoleDTO,
  ClosSessionDTO,
  ClosSessionRoleDTO,
  CreateSessionInput,
  GetSessionInput,
  GetSessionOutput,
  JoinSessionInput,
} from './types.dto';

const MEDIA_ROLES: readonly ClosMediaRoleDTO[] = ['photograph', 'model', 'observer'];

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isFilledString = (value: unknown): value is string =>
  typeof value === 'string' && value.trim() !== '';

const isMediaRole = (value: unknown): value is ClosMediaRoleDTO =>
  MEDIA_ROLES.includes(value as ClosMediaRoleDTO);

@Injectable({
  providedIn: 'root',
})
export class RemoteSessionsApi {
  protected baseUrl = `${environment.api.baseUrl}/v1/room`;

  protected creationOperations = createSessionOperations(() =>
    typeof window === 'undefined' ? undefined : window.sessionStorage,
  );

  constructor(
    protected http: HttpClient,
    protected store: Store,
  ) {}

  createSession(input: CreateSessionInput): Observable<ClosSession> {
    const body = {
      role: input.role,
      is_cloud_images: input.useCloud,
      is_available_for_all: input.public,
      name: input.name,
      type: 'standard',
    };
    const key = `clos:create-room:${this.baseUrl}:${this.store.selectSnapshot(AuthState.user)?.id}`;
    const operationId = environment.roomCreationOperationIds
      ? this.creationOperations.get(key, JSON.stringify(body))
      : undefined;
    return this.http
      .post<{ data: ClosSessionDTO }>(this.baseUrl, {
        ...body,
        ...(operationId ? { operation_id: operationId } : {}),
      })
      .pipe(
        map(({ data }) => data),
        map(RemoteSessionsApi.toClosSession),
        map((session) => ({ ...session, guid: input.guid })),
        tap(() => {
          if (operationId) this.creationOperations.complete(key, operationId);
        }),
      );
  }

  issueSessionInvites(sessionId: string): Observable<NonNullable<ClosSessionDTO['invite_links']>> {
    return this.http
      .post<{
        data: NonNullable<ClosSessionDTO['invite_links']>;
      }>(`${this.baseUrl}/${sessionId}/invites`, {})
      .pipe(map(({ data }) => data));
  }

  joinSession(input: JoinSessionInput): Observable<ClosSession> {
    return this.http
      .post<{
        data: ClosSessionDTO;
      }>(`${this.baseUrl}/${encodeURIComponent(input.sessionId)}/join`, { role: input.role })
      .pipe(
        map(({ data }) => data),
        map(RemoteSessionsApi.toClosSession),
      );
  }

  /**
   * Fresh LiveKit credentials for the same client session, so the participant keeps its identity.
   * Fails when the answer carries no usable LiveKit media: there is nothing to refresh to.
   */
  refreshMediaToken(
    roomId: ClosSession['id'],
    clientSessionId: ClosSessionMedia['clientSessionId'],
  ): Observable<ClosSessionMedia> {
    return this.http
      .post<{ data: unknown }>(`${this.baseUrl}/${encodeURIComponent(roomId)}/media-token`, {
        client_session_id: clientSessionId,
      })
      .pipe(
        map(({ data }) => {
          const media = RemoteSessionsApi.toClosSessionMedia(data);

          if (!media) {
            throw new Error('The media-token answer carries no usable LiveKit media');
          }

          return media;
        }),
      );
  }

  getSession({ sessionId }: GetSessionInput): Observable<GetSessionOutput> {
    return this.http
      .get<{ data: ClosSessionDTO }>(`${this.baseUrl}/${encodeURIComponent(sessionId)}`)
      .pipe(
        map(({ data }) => data),
        map(RemoteSessionsApi.toClosSession),
        map((session) => {
          // uglyhack
          // roomToken is always undefined and media is issued by create, join and media-token only,
          // so both must be removed from the session object
          // or the refresh merge would overwrite what the room is connected with
          const { roomToken: _, media: __, ...rest } = session;

          return { ...rest };
        }),
      );
  }

  static hasShortInviteLinks(links: ClosSessionDTO['invite_links']): boolean {
    // Backend omits occupied/unavailable roles. It verifies the underlying full
    // mapping set before returning these shareable links.
    const roles = Object.values(links?.by_role || {});
    return (
      roles.length > 0 &&
      [links?.generic, ...roles].every(
        (link) => !!link && /^https:\/\/[^/]+\/(?:r\/v1\/)?[A-Za-z0-9_-]{22}$/.test(link),
      )
    );
  }

  /**
   * The backend `media` block as the camelCase model, or `undefined` when it is missing or malformed.
   * `undefined` sends the room down the Twilio path instead of into a connect that cannot work.
   */
  static toClosSessionMedia(dto: unknown): ClosSessionMedia | undefined {
    if (!isRecord(dto)) {
      return undefined;
    }

    const {
      provider,
      url,
      token,
      room,
      media_session_id: mediaSessionId,
      client_session_id: clientSessionId,
      participant_identity: participantIdentity,
      participant_role: participantRole,
      can_publish: canPublish,
      can_subscribe: canSubscribe,
      can_publish_data: canPublishData,
      expires_at: expiresAtDto,
    } = dto;
    const expiresAt = parseRfc3339(expiresAtDto);

    if (
      provider !== 'livekit' ||
      !isFilledString(url) ||
      !isFilledString(token) ||
      !isFilledString(room) ||
      !isFilledString(mediaSessionId) ||
      !isFilledString(clientSessionId) ||
      !isFilledString(participantIdentity) ||
      !isMediaRole(participantRole) ||
      typeof canPublish !== 'boolean' ||
      typeof canSubscribe !== 'boolean' ||
      typeof canPublishData !== 'boolean' ||
      !expiresAt
    ) {
      return undefined;
    }

    return {
      provider,
      url,
      token,
      room,
      mediaSessionId,
      clientSessionId,
      participantIdentity,
      participantRole,
      canPublish,
      canSubscribe,
      canPublishData,
      expiresAt,
    };
  }

  protected static toClosSession(dto: ClosSessionDTO): ClosSession {
    const media = RemoteSessionsApi.toClosSessionMedia(dto.media);

    return {
      id: dto._id,
      name: dto.name,
      roomToken: dto.token,
      ownerId: dto.owner_id,
      ownerName: dto.owner_name,
      photographerId: dto.photograph_id,
      modelId: dto.model_id,
      observers: dto.participants,
      hasShortInviteLinks: RemoteSessionsApi.hasShortInviteLinks(dto.invite_links),
      assignmentLinks: {
        [ClosSessionRoleDTO.model]:
          dto.invite_links?.by_role?.model || dto.links_by_role?.model || '',
        [ClosSessionRoleDTO.photographer]:
          dto.invite_links?.by_role?.photograph || dto.links_by_role?.photograph || '',
        [ClosSessionRoleDTO.observer]:
          dto.invite_links?.by_role?.observer || dto.links_by_role?.observer || '',
      },
      unassignedLink: dto.invite_links?.generic || dto.link,
      link: dto.invite_links?.generic || dto.link,
      filesStorageType: dto.is_cloud_images ? ClosStorage.cloud : ClosStorage.device,
      deepLink: dto.deeplink,
      is_cloud_images: dto.is_cloud_images,
      // The key stays absent for Twilio rooms, so a spread of this session never clears a stored descriptor
      ...(media ? { media } : {}),
    };
  }
}
